<template>
  <SplitViewGridTemplate
    :screenConfig="leftGridConfig"
    :rightConfig="rightGridConfig"
    :backButtonRoute="'/settings/intake'"
    :ref="'intake-variety-size'"
    :defaultUserSettings="false"
  />
</template>

<script>
import { FeatureStorage } from "@/_services/storage";
import SplitViewGridTemplate from "../../../templates/split-view-grid";
import RouteMixin from "@/_mixins/route.mixin";
import { IntakeSettingService } from "../../../_services/api/settings/purchasing/intake-settings.service";
import dxSwitch from "devextreme/ui/switch";
import dxTagBox from "devextreme/ui/tag_box";

export default {
  mixins: [RouteMixin],
  components: {
    SplitViewGridTemplate,
  },

  data() {
    return {
      selectedVariety: null,
      headerSwitchInstance: null,
      currentMode: "review",

      leftGridConfig: {
        title: "Intake Variety Size",
        pageRoute: "/intake-variety-size",
        recordEndPoint: "AlmondcoIntakeVarietySummary",
        exportFileName: "Intake Varieties List",

        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "AlmondcoIntakeVarietySummary",
              filter: [["NumberOfTotalSizes", ">", 0]],
              serverPaging: true,
              allowGrouping: false,
              sort: [{ selector: "Name", desc: false }],
            },
            rowSelect: {
              view: this,
              page: "intake-variety-size",
              mode: "review",
              idProperty: "Code",
              idMode: "String",
            },
          },

          toolbar: {
            search: true,
            columnChooser: true,
            export: false,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                clickAction: () => {
                  this.addNewVariety();
                },
                buttonLocation: "left",
                visible: FeatureStorage.checkPermission(
                  "AlmondcoIntakeSettings",
                  "create"
                ),
              },
            ],
          },

          columns: [
            {
              screenField: "Code",
              label: "Variety",
              dataField: "Name",
              sortOrder: "asc",
              sortIndex: 0,
              allowHiding: false,
              cellTemplate: "GcRecordCell",
              cellTemplateRecord: {
                primary: [
                  {
                    dataField: "Name",
                  },
                ],
                secondary: [
                  {
                    dataField: "Code",
                  },
                ],
              },
              exportFields: [
                {
                  dataField: "Code",
                  label: "Variety Code",
                },
                {
                  dataField: "Name",
                  label: "Variety Name",
                },
              ],
            },
            {
              screenField: "Size #",
              dataField: "NumberOfActiveSizes",
              label: "Size #",
              alignment: "center",
              allowHiding: false,
            },
            {
              screenField: "Product #",
              dataField: "NumberOfActiveProducts",
              label: "Product #",
              alignment: "center",
              allowHiding: false,
            },
            {
              screenField: "Status",
              width: "auto",
              filterIndex: 0,
            },
          ],

          valueChangedEventListners: [],
        },
      },

      rightGridConfig: {
        headerToolbar: {
          closeSplit: true,
          save: FeatureStorage.checkPermission(
            this.$route.meta.applicationFeatureCode,
            "create"
          ),
          edit: true,
          audit: false,
          auditItems: [],
          saveCallBack: () => {
            this.saveRightGridChanges();
          },
          editCallBack: () => {
            this.handleEditMode();
          },
          // Will be set by computed property
          toolbarFormItems: [],
          toolbarFormLocation: "after",
          // Add formContentReady callback to fix switch state after form loads
          formContentReady: (e) => {
            this.onToolbarFormReady(e);
          },
        },

        gridData: {
          grouping: false,
          export: true,
          summary: false,
          exportFileName: "Variety Size Combinations",
          heightPadding: 155,
          paging: false,

          // This function builds the data source based on selected variety
          dataSourceBuilder: (selectedRecord) => {
            this.selectedVariety = selectedRecord;
            return {
              endpoint: `AlmondcoIntakeVarietySizeAll?$filter=VarietyCode eq '${selectedRecord.Code}'`,
              // filter: [["VarietyCode", "=", selectedRecord.Code]],
              expand: `ProductList($select=ProductCode,ProductDescription)`,
              serverPaging: false,
              allowGrouping: false,
              sort: [{ selector: "SizeCode", desc: false }],
            };
          },

          // onEditorPreparing: (e) => {},

          toolbar: {
            search: false,
            columnChooser: false,
            export: false,
            openFilter: false,
            expand: false,
            customButtons: [],
          },
          editingConfig: {
            mode: "cell",
            allowUpdating: FeatureStorage.checkPermission(
              "AlmondcoIntakeSettings",
              "update"
            ),
            allowDeleting: FeatureStorage.checkPermission(
              "AlmondcoIntakeSettings",
              "delete"
            ),
            allowAdding: false,
            startEditAction: "click",
            selectTextOnEditStart: true,
          },
          columns: [],
          valueChangedEventListners: [],
        },
      },
    };
  },

  computed: {
    setSaveOption() {
      return FeatureStorage.checkPermission(
        this.$route.meta.applicationFeatureCode,
        "create"
      );
    },
    // Dynamic toolbar form items that react to mode changes
    toolbarFormItems() {
      return [
        {
          screenField: "Inactive",
          dataField: "InactiveBoolean",
          editorType: "dxSwitch",
          editorOptions: {
            readOnly: this.currentMode === "review",
            disabled: this.currentMode === "review",
            onValueChanged: (e) => {
              const splitViewRef = this.$refs["intake-variety-size"];
              if (splitViewRef && splitViewRef.selectedLeftRecord) {
                splitViewRef.selectedLeftRecord.Inactive = e.value ? "N" : "Y";
              }
              //
              // If variety is being set to Inactive, set all sizes to Inactive as well
              if (!e.value) {
                // Get all items
                const items = this.getRightGridRows();
                items.forEach((item) => {
                  item.Inactive = "Y";
                });
              }

              //
              const isReadOnly = this.currentMode === "review" || !e.value;
              // Update all size grid switches
              this.sizeGridSwitches.forEach((switchInstance) => {
                switchInstance.option("readOnly", isReadOnly);
              });
              // update header switch
              this.headerSwitchInstance.option("readOnly", isReadOnly);
            },
          },
          cssClass: "toolbar-customer-label",
          // This flag tells customizeItem to not override disabled state
          skipDisabledOverride: true,
        },
      ];
    },
    selectedRecordFormItems() {
      if (this.baseView.mode === "create") {
        return [
          {
            itemType: "group",
            caption: "",
            colCount: 1,
            items: [
              {
                dataField: "Code",
                label: { text: "Variety" },
                editorType: "dxSelectBox",
                editorOptions: {
                  dataSource: IntakeSettingService.dataSourceConfig(
                    "api/AlmondcoIntakeVarietySummary",
                    {
                      sort: [{ selector: "Code", asc: true }],
                      filter: [["NumberOfTotalSizes", "=", 0]],
                    }
                  ),
                  valueExpr: "Code",
                  displayExpr: (item) => {
                    if (item) return item.Code + " - " + item.Name;
                    else return "";
                  },
                  searchEnabled: true,
                  showClearButton: true,
                  onValueChanged: this.handleVarietySelection,
                },
                validationRules: [
                  {
                    type: "required",
                    message: "Variety is required",
                  },
                ],
              },
            ],
          },
        ];
      } else {
        // Review and Edit modes
        return [
          {
            itemType: "group",
            caption: "",
            colCount: 1,
            items: [
              {
                dataField: "CodeName",
                label: { text: "Variety" },
                editorType: "dxTextBox",
                editorOptions: {
                  readOnly: true,
                },
              },
            ],
          },
        ];
      }
    },

    rightGridColumns() {
      const vm = this;
      return [
        {
          screenField: "Inactive",
          dataField: "Inactive",
          width: 100,
          dataType: "boolean",
          editorType: "dxSwitch",
          allowEditing: true,
          headerCellTemplate: (container) => {
            const headerDiv = document.createElement("div");
            headerDiv.style.display = "flex";
            headerDiv.style.alignItems = "center";
            headerDiv.style.justifyContent = "space-between";
            headerDiv.style.width = "100%";

            const switchDiv = document.createElement("div");
            switchDiv.style.marginLeft = "5px";

            const switchInstance = new dxSwitch(switchDiv, {
              value: false,
              readOnly:
                vm.currentMode === "review" ||
                vm.selectedVariety.Inactive == "Y",
              hint: "Toggle all active states",
              onValueChanged: (e) => {
                if (e.event) {
                  const newState = e.value;
                  vm.toggleAllActiveStatesTo(newState);
                }
              },
            });

            vm.headerSwitchInstance = switchInstance;
            headerDiv.appendChild(switchDiv);
            container.appendChild(headerDiv);
          },
          calculateCellValue: (rowData) => {
            return rowData.Inactive === "N";
          },
          editCellTemplate: (cellElement, cellInfo) => {
            const switchDiv = document.createElement("div");
            const switchInstance = new dxSwitch(switchDiv, {
              value: cellInfo.value,
              readOnly:
                vm.currentMode === "review" ||
                vm.selectedVariety.Inactive == "Y",
              disabled: cellInfo.data.SizeInactive == "Y",
              onValueChanged: (e) => {
                e.component.beginUpdate();
                cellInfo.data.Inactive = e.value ? "N" : "Y";
                e.component.endUpdate();
              },
            });
            // Store the switch instance reference so we can update it
            if (!vm.sizeGridSwitches) {
              vm.sizeGridSwitches = [];
            }
            vm.sizeGridSwitches.push(switchInstance);

            cellElement.appendChild(switchDiv);
          },
        },
        {
          screenField: "Code",
          dataField: "SizeCode",
          label: "Size",
          allowHiding: false,
          allowEditing: false,
          width: 80,
        },
        {
          dataField: "ProductList",
          editorType: "dxTagBox",
          allowEditing: true,
          multiSelect: true,
          cellTemplate: (container, options) => {
            const productList = options.data.ProductList || [];
            if (productList.length === 0) {
              return;
            }
            const tagsContainer = document.createElement("div");
            tagsContainer.style.display = "flex";
            tagsContainer.style.flexWrap = "wrap";
            tagsContainer.style.gap = "5px";

            productList.forEach((product) => {
              const tag = document.createElement("div");
              tag.style.display = "inline-flex";
              tag.style.alignItems = "center";
              tag.style.backgroundColor = "#f0f0f0";
              tag.style.border = "1px solid #d9d9d9";
              tag.style.borderRadius = "2px";
              tag.style.padding = "2px 4px";
              tag.style.color = "#333";
              // tag.style.maxWidth = "200px";
              tag.style.overflow = "hidden";
              tag.style.textOverflow = "ellipsis";
              tag.style.whiteSpace = "nowrap";

              const productText = document.createElement("span");
              productText.textContent =
                product.ProductCode + " - " + product.ProductDescription;
              productText.title =
                product.ProductCode + " - " + product.ProductDescription;
              tag.appendChild(productText);

              tagsContainer.appendChild(tag);
            });
            container.appendChild(tagsContainer);
          },
          calculateCellValue: (rowData) => {
            if (!rowData.ProductList || !Array.isArray(rowData.ProductList)) {
              return [];
            }
            return rowData.ProductList.map((p) => p.ProductCode);
          },
          editCellTemplate: (container, options) => {
            // Gather already added products from other rows to exclude them
            const gridInstance =
              this.$refs["intake-variety-size"].$refs[
                "split-view-right-grid"
              ].getDataSourceInstance();
            let addedProducts = [];

            // Loop through all rows except the current one
            if (gridInstance) {
              const getDataSource = gridInstance.getDataSource();
              const dataItems = getDataSource.items();
              const filteredItems = dataItems.filter(
                (item) => item.RecordId !== options.data.RecordId
              );
              // Collect ProductCodes from the other rows
              filteredItems.forEach((item) => {
                if (item.ProductList && Array.isArray(item.ProductList)) {
                  item.ProductList.forEach((product) => {
                    addedProducts.push(product.ProductCode);
                  });
                }
              });
            }
            // Create a custom TagBox instance with better control over data
            const tagBox = document.createElement("div");
            container.appendChild(tagBox);

            // Initialize the TagBox with proper event handling
            new dxTagBox(tagBox, {
              dataSource: IntakeSettingService.dataSourceConfig(
                "api/ProductsNotInIntakeVarietySize?includeRecordId=" +
                  options.data.RecordId,
                {
                  sort: [{ selector: "Description1", asc: true }],
                  select: ["Code", "Description1"],
                  postProcess: (data) => {
                    // Exclude already added products
                    return data.filter(
                      (item) => !addedProducts.includes(item.Code)
                    );
                  },
                }
              ),
              valueExpr: "Code",
              displayExpr: (item) => {
                if (item) return item.Code + " - " + item.Description1;
                else return "";
              },
              showClearButton: true,
              searchExpr: ["Code", "Description1"],
              searchMode: "contains",
              searchEnabled: true,
              readOnly: vm.currentMode === "review",
              disabled: options.data.SizeInactive == "Y",
              value: options.value,
              onValueChanged: (e) => {
                if (vm.currentMode === "review") {
                  return;
                }
                //
                const selectedItems = e.component.option("selectedItems");
                //
                options.data.ProductList = selectedItems.map((item) => ({
                  ProductCode: item.Code,
                  ProductDescription: item.Description1,
                }));
              },
              dropDownOptions: {
                width: 350,
              },
              itemTemplate: (data) => {
                return ` <div class="custom-auto-complete" id="${data.Code}" >
                  <span class="auto-complete-primary">
                    ${data.Code}
                  </span>
                  <span class="record-secondary gc-grid-code">
                    ${data.Description1}
                  </span> 
                </div>`;
              },

              onFocusIn: (e) => {
                e.event.target.select();
                e.component.open();
              },
            });
          },
          // showEditorAlways: true,
        },
      ];
    },
  },

  methods: {
    onToolbarFormReady(e) {
      // After the toolbar form is ready, manually set the switch to enabled if in edit mode
      if (e && e.component) {
        const editor = e.component.getEditor("InactiveBoolean");
        if (editor) {
          const shouldBeDisabled = this.currentMode === "review";
          editor.option("disabled", shouldBeDisabled);
          editor.option("readOnly", shouldBeDisabled);
        }
      }
    },
    handleVarietySelection(e) {
      if (e.value) {
        this.selectedVariety = e.component.option("selectedItem");
        // Update header inactive button (variety inactive)
        const splitViewRef = this.$refs["intake-variety-size"];
        if (splitViewRef && splitViewRef.selectedLeftRecord) {
          splitViewRef.selectedLeftRecord.InactiveBoolean =
            this.selectedVariety.Inactive === "N";
          // Force toolbar re-render by incrementing toolbarKey
          splitViewRef.toolbarKey++;
        }
        //
        this.loadRightGridForSelectedVariety();
      }
    },
    loadRightGridForSelectedVariety() {
      if (this.selectedVariety && this.selectedVariety.Code) {
        const splitViewRef = this.$refs["intake-variety-size"];
        if (splitViewRef) {
          splitViewRef.loadRightGridData(this.selectedVariety);
        }
      }
    },
    addNewVariety() {
      // Navigate to create new variety page or open modal
      this.$router.push({
        path: `${this.leftGridConfig.pageRoute}/create`,
      });
    },

    handleEditMode() {
      if (this.selectedVariety && this.selectedVariety.Code) {
        this.currentMode = "edit";

        if (this.headerSwitchInstance) {
          this.headerSwitchInstance.option("readOnly", false);
        }

        this.rightGridConfig.gridData.columns = this.rightGridColumns;

        // Don't navigate here, let the split-view-grid handle it
        const splitViewRef = this.$refs["intake-variety-size"];
        if (splitViewRef) {
          splitViewRef.switchToEditMode();
        }
      }
    },

    toggleAllActiveStatesTo(activeState) {
      let rows = this.getRightGridRows();
      const newState = activeState ? "N" : "Y";

      // Update all rows
      rows.forEach((row) => {
        // if the size is inactive, keep it inactive
        if (row.SizeInactive === "Y") {
          row.Inactive = "Y";
        } else {
          row.Inactive = newState;
        }
      });
    },

    getRightGridRows() {
      const splitViewRef = this.$refs["intake-variety-size"];
      const gridRef = splitViewRef.$refs["split-view-right-grid"];
      return gridRef.getDataSource();
    },

    async saveRightGridChanges() {
      let splitViewRef = this.$refs["intake-variety-size"];
      try {
        splitViewRef.showLoading(true);
        //
        let items = this.getRightGridRows();
        //
        const payload = {
          VarietyCode: this.selectedVariety.Code,
          VarietyInactive: this.selectedVariety.Inactive,
          //
          SizeCombinations: items.map((item) => ({
            SizeCode: item.SizeCode,
            SizeInactive: item.Inactive,
            Products: item.ProductList
              ? item.ProductList.map((product) => ({
                  ProductCode: product.ProductCode,
                }))
              : [],
          })),
        };
        //
        splitViewRef.showLoading(true);
        //
        await IntakeSettingService.saveIntakeVarietySizeCombination(payload)
          .then(() => {
            this.setIgnoreRouteChangeConfirmation();
            this.$router.push({
              path: "/intake-variety-size/review/" + this.selectedVariety.Code,
            });
          })
          .catch((err) => {
            splitViewRef.showLoading(false);
            this.displayServerErrorOnTemplate(err, "Save Failed");
          });

        // Refresh the left grid to show updated counts
        splitViewRef.refreshLeftGrid();
      } catch (err) {
        // console.error("Save error:", err);
      } finally {
        splitViewRef.showLoading(false);
      }
    },

    displayServerErrorOnTemplate(err, msg) {
      this.$store.dispatch("ui/setServerError", {
        error: err,
        title: msg,
      });
    },
  },

  watch: {
    "baseView.mode": {
      handler(newMode) {
        this.currentMode = newMode;

        // Update toolbar form items with new readOnly state
        this.rightGridConfig.headerToolbar.toolbarFormItems =
          this.toolbarFormItems;

        if (this.headerSwitchInstance) {
          this.headerSwitchInstance.option("readOnly", newMode === "review");
          this.headerSwitchInstance.option("disabled", newMode === "review");
        }

        if (this.rightGridConfig && this.rightGridConfig.gridData) {
          this.rightGridConfig.gridData.columns = this.rightGridColumns;
        }
      },
      immediate: true,
    },
  },

  created() {
    this.rightGridConfig.gridData.columns = this.rightGridColumns;
    this.rightGridConfig.headerToolbar.toolbarFormItems = this.toolbarFormItems;
  },
};
</script>
